import { useCallback, useEffect, useRef, useState } from 'react';

import { HttpRequestException } from '../api/client';
import {
  endSession as apiEndSession,
  resolveSession,
  type Session,
  sendStatistics,
} from '../api/session';
import {
  isSessionAccessDenied,
  redirectSessionTerminal,
} from '../utils/sessionRedirect';

interface UseSessionOptions {
  onError?: (err: string) => void;
}

const hasSessionIdInUrl = (): boolean =>
  new URLSearchParams(window.location.search).has('sessionID');

export const useSession = ({ onError }: UseSessionOptions = {}) => {
  const [session, setSession] = useState<Session | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(hasSessionIdInUrl);

  const onErrorRef = useRef(onError);
  const sessionRef = useRef<Session | null>(null);

  useEffect(() => {
    onErrorRef.current = onError;
  });

  const updateSession = (next: Session | null) => {
    sessionRef.current = next;
    setSession(next);
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('sessionID');

    if (!sessionId || params.get('finished') === 'true') return;

    setLoading(true);
    let isMounted = true;
    let timeoutId: ReturnType<typeof setTimeout>;

    const pollSession = async () => {
      try {
        const data = await resolveSession(sessionId);
        if (!isMounted) return;

        if (data.status === 'finished' || data.status === 'failed') {
          redirectSessionTerminal(
            data.properties?.seastarLocation,
            'SessionEnd',
          );
          return;
        }

        // FR-SESSION-6: Mobile unsupported
        const isMobile =
          /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
            navigator.userAgent,
          );
        if (isMobile) {
          console.warn('Mobile devices are not supported. Ending session.');
          setError('Mobile Device Not Supported');
          setLoading(false);

          try {
            await apiEndSession(sessionId);
            sendStatistics(sessionId, { error: 'Mobile Device' });
            redirectSessionTerminal(
              data.properties?.seastarLocation,
              'SessionError',
            );
          } catch (e) {
            console.error('Failed to end session for mobile device:', e);
          }
          return;
        }

        updateSession(data);

        if (data.windows && data.windows.length > 0) {
          setLoading(false);
        }

        const shouldKeepPolling =
          !data.windows?.length || data.status !== 'running';

        if (shouldKeepPolling) {
          timeoutId = setTimeout(pollSession, 1000);
        }
      } catch (err: unknown) {
        if (!isMounted) return;

        // FR-SESSION-7: Preserve 502 tolerance
        if (err instanceof HttpRequestException && err.status === 502) {
          console.warn('502 Bad Gateway during polling, retrying...');
          timeoutId = setTimeout(pollSession, 1000);
          return;
        }

        if (isSessionAccessDenied(err)) {
          redirectSessionTerminal(
            sessionRef.current?.properties?.seastarLocation,
            'SessionEnd',
          );
          return;
        }

        console.error(err);
        const errMsg = err instanceof Error ? err.message : String(err);
        setError(errMsg);
        setLoading(false);
        onErrorRef.current?.(errMsg);
      }
    };

    pollSession();

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, []);

  const expiration = session?.expiration;
  const seastarLocation = session?.properties?.seastarLocation;

  useEffect(() => {
    if (!expiration) return;

    const timeUntilExpiration = expiration * 1000 - Date.now();

    const handleExpiration = () => {
      redirectSessionTerminal(seastarLocation, 'SessionExpire');
    };

    if (timeUntilExpiration <= 0) {
      handleExpiration();
      return;
    }

    const timeoutId = setTimeout(handleExpiration, timeUntilExpiration);
    return () => clearTimeout(timeoutId);
  }, [expiration, seastarLocation]);

  const endSession = useCallback(async () => {
    if (!session) return;

    try {
      await apiEndSession(session.id);
      sendStatistics(session.id, {});
      redirectSessionTerminal(session.properties.seastarLocation, 'SessionEnd');
    } catch (err: unknown) {
      console.error('Failed to end session:', err);
      const errMsg =
        err instanceof Error ? err.message : 'Failed to end session';
      setError(errMsg);
      onErrorRef.current?.(errMsg);
    }
  }, [session]);

  return { session, loading, error, endSession };
};
